import { ReactNode, useEffect, useRef, useState } from 'react';
import { Alert, Box, Card, Chip, Typography, useMediaQuery, useTheme } from '@mui/material';
import type { CommunicationInputData } from '../../../../hooks/Marketing';
import { useLanguage } from '../../../../context/LanguageContext';
import CampaignDefinitionStep from './CampaignDefinitionStep';
import CampaignTargetingStep, { TargetingStepHandle } from './CampaignTargetingStep';
import CampaignCreationStep, { CreationStepHandle } from './CampaignCreationStep';
import CampaignOffersStep from './CampaignOffersStep';
import CampaignValidationStep from './CampaignValidationStep';
import ChangeChannelDialog from './ChangeChannelDialog';
import BatPreviewDialog from './BatPreviewDialog';
import type { EmailUnlayerHandle } from './EmailUnlayerSection';
import RichSmsStep from './editor/RichSmsStep';
import { preloadUnlayer } from './creation/email/preloadUnlayer';
import StepsBar from './editor/StepsBar';
import EditorFooter from './editor/EditorFooter';
import ValidationIssuesAlert from './editor/ValidationIssuesAlert';
import VerifyRecapDialog from './editor/VerifyRecapDialog';
import { StepKey, useCampaignSteps } from './editor/useCampaignSteps';
import { useCampaignForm } from './editor/useCampaignForm';
import { usePersistCampaign } from './editor/usePersistCampaign';
import { useValidationActions } from './editor/useValidationActions';
import { definitionErrors, firstError, offerErrors } from './validation/fieldErrors';

interface CampaignEditorProps {
  editingComm?: CommunicationInputData | null;
  // Appelé dès que la campagne existe en base (premier enregistrement), pour que la page mette l'URL à jour
  onCreated?: (communicationInputId: number) => void;
  onDraftSaved?: (communicationInputId: number) => void;
  onValidated?: (communicationInputId: number) => void;
  // Actions de page affichées à droite de l'en-tête (ex. « Nouvelle campagne » en édition)
  headerActions?: ReactNode;
}

function CampaignEditor({ editingComm, onCreated, onDraftSaved, onValidated, headerActions }: CampaignEditorProps) {
  const { t } = useLanguage();
  const theme = useTheme();
  const compact = useMediaQuery(theme.breakpoints.down('md'));
  const creationStepRef = useRef<CreationStepHandle>(null);
  const targetingStepRef = useRef<TargetingStepHandle>(null);
  const richLandingRef = useRef<EmailUnlayerHandle>(null);
  const [launchingCount, setLaunchingCount] = useState(false);

  const form = useCampaignForm(editingComm);
  const { definition, targeting, creationPayloadRef } = form;
  const steps = useCampaignSteps(
    { channel: definition.channel, hasOffer: definition.hasOffer, isRichSms: form.isRichSms, noCreation: form.hasNoCreation },
    form.creationResetKey,
  );
  const persistence = usePersistCampaign({ form, creationStepRef, richLandingRef });
  const { actions, batPreview } = useValidationActions({ definition, targeting, creationPayloadRef, creationStepRef });
  const savedId = persistence.communicationInputId;

  const hasUnsavedData = persistence.isSaved ? form.isDirty : definition.title.trim() !== '' || definition.channel !== '';

  useEffect(() => {
    if (savedId !== null && savedId !== editingComm?.id) onCreated?.(savedId);
  }, [savedId, editingComm?.id, onCreated]);

  useEffect(preloadUnlayer, []);

  // Plus de dialog à fermer : on prévient seulement avant de quitter la page avec des modifications non enregistrées
  useEffect(() => {
    if (!hasUnsavedData) return;
    const warn = (event: BeforeUnloadEvent) => event.preventDefault();
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [hasUnsavedData]);

  const navigate = (index: number) => {
    steps.goToStep(index);
    persistence.clearDraftError();
  };

  const handleNext = async () => {
    if (steps.isLastStep) return;
    const targetingStep = targetingStepRef.current;
    if (steps.activeKey === 'ciblage' && targetingStep?.hasPendingTargeting()) {
      setLaunchingCount(true);
      try {
        if (!(await targetingStep.launchCount())) return;
      } finally {
        setLaunchingCount(false);
      }
    }
    navigate(steps.activeStep + 1);
  };

  const renderStep = (key: StepKey) => {
    switch (key) {
      case 'definition':
        return <CampaignDefinitionStep data={definition} onChange={form.changeDefinition} />;
      case 'creation':
        return (
          <CampaignCreationStep
            key={form.creationResetKey}
            ref={creationStepRef}
            channel={definition.channel}
            onHasContent={form.setHasCreation}
            offerCount={form.creationOffers.count}
            offerTitles={form.creationOffers.titles}
            onAddOffer={form.creationOffers.add}
            onOfferUsed={form.creationOffers.markUsed}
            onOfferIndicesChange={form.creationOffers.setUsed}
            onOfferRemoved={form.creationOffers.remove}
            initialPayload={form.initialCreationPayload}
            onPayloadChange={form.changeCreationPayload}
          />
        );
      case 'creationRich':
        return <RichSmsStep ref={richLandingRef} payload={creationPayloadRef.current ?? form.initialCreationPayload} />;
      case 'ciblage':
        return (
          <CampaignTargetingStep
            ref={targetingStepRef}
            data={targeting}
            onChange={form.changeTargeting}
            isCashdesk={definition.channel === 'cashdesk'}
          />
        );
      case 'offers':
        return (
          <CampaignOffersStep
            offers={form.offers}
            onOffersChange={form.changeOffers}
            usedOfferIndices={form.usedOfferIndices}
            isRecurring={definition.isRecurring}
            isPreEdited={definition.isPreEdited}
            isCashdesk={definition.channel === 'cashdesk'}
          />
        );
      case 'validation':
        return (
          <CampaignValidationStep
            definition={definition}
            targeting={targeting}
            offers={form.offers}
            hasCreation={form.hasCreation}
            creationPayload={creationPayloadRef.current}
            onTestOperationChange={isTestOperation => form.changeDefinition({ isTestOperation })}
            acknowledgements={form.acknowledgements}
            actions={actions}
          />
        );
    }
  };

  const title = definition.title.trim();
  // Le canal conditionne toutes les étapes suivantes : tant qu'il n'est pas choisi, on reste sur la définition
  const channelMissingReason = definition.channel === '' ? t('pages.marketing.communication.selectChannelFirst') : null;
  // Une saisie invalide (ex. date dans le passé) est signalée en rouge sur le champ et bloque l'étape suivante
  const isCashdeskChannel = definition.channel === 'cashdesk';
  const stepFieldError =
    steps.activeKey === 'definition' ? firstError(definitionErrors(definition))
      : steps.activeKey === 'offers' ? firstError(Object.assign({}, ...form.offers.map(o => offerErrors(o, { isRecurring: definition.isRecurring, isCashdesk: isCashdeskChannel }))))
        : null;
  const nextBlockedReason = channelMissingReason ?? stepFieldError;
  // L'éditeur email (Unlayer) met plusieurs secondes à démarrer : on monte l'étape Création, invisible,
  // dès que le canal Email est choisi, pour qu'il soit prêt quand on y arrive
  const preloadsInBackground = (key: StepKey) => key === 'creation' && definition.channel === 'email';
  // Une étape préchargée reste rendue hors de vue à pleine largeur (et non en display: none, où l'iframe
  // de l'éditeur n'aurait aucune taille pour s'initialiser)
  const stepSx = (key: StepKey) => {
    if (key === steps.activeKey) return { display: 'block' };
    if (preloadsInBackground(key)) {
      return { position: 'absolute', top: 0, left: 0, right: 0, height: 0, overflow: 'hidden', visibility: 'hidden', pointerEvents: 'none' } as const;
    }
    return { display: 'none' };
  };

  return (
    <>
      <Card
        square
        elevation={0}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          flex: '1 1 auto',
          height: { xs: 'auto', md: '100%' },
          minHeight: { xs: 560, md: 0 },
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            flexWrap: { xs: 'wrap', md: 'nowrap' },
            columnGap: 2,
            px: 3,
            borderBottom: '1px solid',
            borderColor: 'grey.200',
            flexShrink: 0,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0, py: 1 }}>
            <Typography variant="subtitle1" fontWeight={700} noWrap sx={{ minWidth: 0, maxWidth: 320 }}>
              {title || 'Campagne sans titre'}
            </Typography>
            <Chip
              size="small"
              label={persistence.isSaved ? (form.isDirty ? 'Modifications non enregistrées' : 'Brouillon enregistré') : 'Non enregistrée'}
              color={persistence.isSaved && !form.isDirty ? 'success' : 'default'}
              variant="outlined"
              sx={{ fontSize: '0.7rem', flexShrink: 0 }}
            />
          </Box>
          <Box sx={{ flex: '1 1 auto', minWidth: 0, display: 'flex', order: { xs: 3, md: 0 }, width: { xs: '100%', md: 'auto' }, ml: { md: 2 } }}>
            <StepsBar steps={steps.visibleSteps} activeStep={steps.activeStep} onChange={navigate} compact={compact} lockedReason={channelMissingReason} />
          </Box>
          {headerActions && <Box sx={{ flexShrink: 0, ml: 'auto', py: 1 }}>{headerActions}</Box>}
        </Box>

        <Box sx={{ display: 'flex', flex: '1 1 auto', minHeight: 0 }}>
          {/* Gouttière réservée : l'apparition de la scrollbar (ex. ajout d'une offre) ne décale plus le contenu */}
          <Box sx={{ flexGrow: 1, overflow: 'auto', scrollbarGutter: 'stable', minWidth: 0, position: 'relative' }}>
            {persistence.saveError && <Alert severity="error" sx={{ m: 2 }}>{persistence.saveError}</Alert>}
            {steps.visibleSteps.map(({ key }) => (steps.isMounted(key) || preloadsInBackground(key)) && (
              <Box key={key} sx={stepSx(key)}>
                {renderStep(key)}
              </Box>
            ))}
          </Box>
        </Box>

        <EditorFooter
          alerts={
            <>
              {persistence.validationIssues.length > 0 && (
                <ValidationIssuesAlert
                  issues={persistence.validationIssues}
                  canFix={steps.hasStep}
                  onFix={steps.goToKey}
                  onClose={persistence.clearValidationIssues}
                />
              )}
              {persistence.draftError && (
                <Alert severity="error" onClose={persistence.clearDraftError} sx={{ mb: 1, py: 0.25 }}>
                  {persistence.draftError}
                </Alert>
              )}
            </>
          }
          canGoBack={steps.activeStep > 0}
          onBack={() => navigate(steps.activeStep - 1)}
          onSaveDraft={async () => {
            const commId = await persistence.saveDraft();
            if (commId !== null) onDraftSaved?.(commId);
          }}
          savingDraft={persistence.saving}
          terminating={persistence.verifying}
          creating={persistence.creating}
          isLastStep={steps.isLastStep}
          onNext={handleNext}
          launchingCount={launchingCount}
          onTerminate={persistence.terminate}
          terminateBlockedReason={form.terminateBlockedReason}
          nextBlockedReason={nextBlockedReason}
        />
      </Card>

      <VerifyRecapDialog
        open={persistence.recap !== null}
        snapshot={persistence.recap?.snapshot ?? null}
        validating={persistence.validating}
        onClose={persistence.closeRecap}
        onConfirm={async () => {
          const commId = persistence.recap?.commId ?? null;
          if ((await persistence.confirmValidation()) && commId !== null) onValidated?.(commId);
        }}
      />

      <BatPreviewDialog open={batPreview.open} onClose={batPreview.onClose} content={batPreview.content} channel={batPreview.channel} />

      <ChangeChannelDialog open={form.pendingChannel !== null} onCancel={form.cancelChannelChange} onConfirm={form.confirmChannelChange} />
    </>
  );
}

export default CampaignEditor;
